import { Grid } from 'antd';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import HTMLFlipBook from 'react-pageflip';

import Headings from '../landingPage/heading';
import { ShouldRender } from '../../shared/basic/ShouldRender';

const { useBreakpoint } = Grid;

interface SneakPeekSectionProps {
  data: string[];
}

const SneakPeekSection: React.FC<SneakPeekSectionProps> = ({ data }) => {
  const screens = useBreakpoint();
  const flipBookRef = useRef<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      handlePageChange(2 as number);
    }
  }, [inView]);

  const handlePageChange = (pageIndex: number) => {
    if (currentPage !== pageIndex) {
      setCurrentPage(pageIndex);
    }

    if (flipBookRef.current) {
      const pageFlipInstance = flipBookRef.current.pageFlip?.();
      if (pageFlipInstance?.flip) {
        pageFlipInstance.flip(pageIndex);
      }
    }

    if (!isOpen) setIsOpen(true);
  };

  const groupedPages = [];
  for (let i = 0; i < data?.length; i += 2) {
    groupedPages.push([i, i + 1]);
  }

  const pages = data?.map((src, index) => (
    <div key={`${src}-${index}`} className='w-1/2 rounded-lg'>
      <div className='relative h-full w-full flex rounded-md overflow-hidden'>
        <ShouldRender check={data[index]}>
          <Image
            src={src as string}
            alt={`Page ${index}`}
            fill
            style={{ objectFit: 'cover' }}
            className='rounded-md w-full'
          />
          <div
            style={{
              background:
                'linear-gradient(to right, transparent 0%,rgba(0,0,0,0.2) 46%,rgba(0,0,0,0.5) 49%,rgba(0,0,0,0.6) 50%,rgba(0,0,0,0.5) 51%,rgba(0,0,0,0.2) 52%,transparent 100%)',
            }}
            className={clsx(
              index % 2 === 0 ? '-right-10' : '-left-10',
              'absolute h-full w-20 z-40',
            )}
          ></div>
        </ShouldRender>
      </div>
    </div>
  ));

  return (
    <div className='container mx-auto'>
      <Headings heading='TAKE A SNEAK PEEK' />
      <div
        style={{ position: 'relative' }}
        ref={inViewRef}
        className='customCarousel bg-readerLightGrayBg pt-6 pb-8 md:pb-10 flex px-4 md:px-10 rounded-lg overflow-hidden'
      >
        <HTMLFlipBook
          ref={flipBookRef}
          width={screens.xs ? 150 : 250}
          height={screens.xs ? 250 : 350}
          size='stretch'
          maxShadowOpacity={0.5}
          mobileScrollSupport={false}
          showCover={false}
          drawShadow={true}
          usePortrait={screens.xs}
          flippingTime={1000}
          onFlip={(e: any) => {
            handlePageChange(e.data);
          }}
        >
          {pages}
        </HTMLFlipBook>

        <div
          style={{
            position: 'absolute',
            bottom: 15,
            left: 0,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}
          >
            {groupedPages.map((pagePair, index) => (
              <motion.div
                key={`${pagePair}-${index}`}
                onClick={() => handlePageChange(pagePair[0] as number)}
                initial={{
                  width:
                    currentPage === pagePair[0] || currentPage === pagePair[1]
                      ? 30
                      : 10,
                }}
                animate={{
                  width:
                    currentPage === pagePair[0] || currentPage === pagePair[1]
                      ? 30
                      : 10,
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{
                  background:
                    currentPage === pagePair[0] || currentPage === pagePair[1]
                      ? 'white'
                      : 'grey',
                  borderRadius: '5px',
                  height: '5px',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SneakPeekSection;
